import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaWallet, FaChartLine, FaRupeeSign, FaArrowRight, FaCalendarAlt, FaUser, FaHistory, FaArrowUp, FaShieldAlt, FaTrophy, FaRocket } from 'react-icons/fa';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const InvestorDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [dashboardData, setDashboardData] = useState({
    totalInvested: 0,
    totalReturns: 0,
    activeInvestments: 0,
    walletBalance: 0,
    recentInvestments: [],
    chartData: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/dashboard/investor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data.dashboard);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { 
      title: 'Total Invested', 
      value: `₹${dashboardData.totalInvested.toLocaleString()}`, 
      icon: <FaRupeeSign className="text-white text-2xl" />, 
      change: '+12%', 
      color: 'from-blue-500 to-blue-600',
      desc: 'Total amount invested'
    },
    { 
      title: 'Total Returns', 
      value: `₹${dashboardData.totalReturns.toLocaleString()}`, 
      icon: <FaArrowUp className="text-white text-2xl" />, 
      change: '+8%', 
      color: 'from-green-500 to-green-600',
      desc: 'Total profit earned'
    },
    { 
      title: 'Active Investments', 
      value: dashboardData.activeInvestments, 
      icon: <FaCalendarAlt className="text-white text-2xl" />, 
      change: '+2', 
      color: 'from-purple-500 to-purple-600',
      desc: 'Currently active'
    },
    { 
      title: 'Wallet Balance', 
      value: `₹${dashboardData.walletBalance.toLocaleString()}`, 
      icon: <FaWallet className="text-white text-2xl" />, 
      change: '+5%', 
      color: 'from-orange-500 to-orange-600',
      desc: 'Available to withdraw'
    },
    { 
      title: 'Daily Profit', 
      value: `₹${(dashboardData.dailyProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
      icon: <FaChartLine className="text-white text-2xl" />, 
      change: 'Active', 
      color: 'from-indigo-500 to-indigo-600',
      desc: 'Daily earnings from active plans'
    },
  ];

  const COLORS = ['#22c55e', '#eab308', '#3b82f6', '#ef4444'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Investor Dashboard - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-8">
        <div className="container mx-auto px-4">
          {/* Welcome Section - Improved */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Welcome back, {user?.name?.split(' ')[0]}!</h1>
                <p className="text-gray-500">Here's your investment portfolio overview</p>
              </div>
            </div>
          </div>

          {/* Stats Cards - Improved */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, idx) => (
              <div key={idx} className={`bg-gradient-to-r ${stat.color} rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition duration-300`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm opacity-90">{stat.title}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    <p className="text-xs opacity-80 mt-2">↑ {stat.change} from last month</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-2">
                    {stat.icon}
                  </div>
                </div>
                <p className="text-xs opacity-70 mt-3">{stat.desc}</p>
              </div>
            ))}
          </div>

          {/* Charts Section - Improved */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Profit Growth Chart */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800">📈 Profit Growth</h2>
                <span className="text-xs text-gray-400">Last 6 months</span>
              </div>
              {dashboardData.chartData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dashboardData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="profit" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center bg-gray-50 rounded-lg">
                  <FaChartLine className="text-gray-300 text-5xl mb-3" />
                  <p className="text-gray-400">No data yet. Start investing to see growth!</p>
                </div>
              )}
            </div>

            {/* Investment Distribution Chart */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800">🥧 Investment Distribution</h2>
                <span className="text-xs text-gray-400">By plan</span>
              </div>
              {dashboardData.recentInvestments?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Basic Plan', value: 60 },
                        { name: 'Standard Plan', value: 40 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      <Cell fill={COLORS[0]} />
                      <Cell fill={COLORS[1]} />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center bg-gray-50 rounded-lg">
                  <FaChartLine className="text-gray-300 text-5xl mb-3" />
                  <p className="text-gray-400">No data yet. Start investing to see distribution!</p>
                </div>
              )}
              <div className="flex justify-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Basic Plan</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Standard Plan</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Investments Table - Improved */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">📋 Recent Investments</h2>
              {dashboardData.recentInvestments?.length > 0 && (
                <Link to="/investor/my-investments" className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1">
                  View All <FaArrowRight size={12} />
                </Link>
              )}
            </div>
            <div className="overflow-x-auto">
              {dashboardData.recentInvestments?.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-gray-400 mb-3">
                    <FaHistory className="text-5xl mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No Investments Yet</h3>
                  <p className="text-gray-500 mb-4">Start your investment journey today</p>
                  <Link to="/investor/plans" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition inline-block">
                    Explore Investment Plans
                  </Link>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Returns</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dashboardData.recentInvestments.map((inv, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 capitalize font-medium">{inv.plan}</td>
                        <td className="px-6 py-4 font-semibold">₹{inv.amount?.toLocaleString()}</td>
                        <td className="px-6 py-4 text-gray-600">{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            inv.status === 'active' ? 'bg-green-100 text-green-800' : 
                            inv.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-green-600 font-semibold">{inv.interestRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Call to Action - For new users */}
          {dashboardData.totalInvested === 0 && (
            <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 text-center border border-green-200">
              <div className="flex justify-center gap-4 mb-4">
                <FaTrophy className="text-yellow-500 text-4xl" />
                <FaRocket className="text-green-600 text-4xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Start Your Investment Journey?</h3>
              <p className="text-gray-600 mb-4">Invest in Assets Dairy Farm and get guaranteed 60% returns</p>
              <Link to="/investor/plans" className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition inline-flex items-center gap-2 shadow-md">
                Invest Now <FaArrowRight />
              </Link>
            </div>
          )}

          {/* Security Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
              <FaShieldAlt className="text-green-500" /> Your investments are secured with 100% capital protection
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestorDashboard;